import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categoriesFull } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'dianshiju');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const activeCategory = categoriesFull.find((c) => c.slug === activeSlug);
  const categoryVideos = videos.filter((v) => v.category === activeSlug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory?.name || '分类'} - 蘑菇视频网页版`,
    description: activeCategory?.description || '浏览蘑菇视频网页版的视频分类内容',
    url: `https://www.moguvideo.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '蘑菇视频网页版',
      url: 'https://www.moguvideo.com/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.moguvideo.com/' },
      { '@type': 'ListItem', position: 2, name: activeCategory?.name || '分类', item: `https://www.moguvideo.com/category/${activeSlug}` },
    ],
  };

  return (
    <>
      <SchemaOrg schema={categorySchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">{activeCategory?.name || '分类'}</span>
            </nav>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                {activeCategory?.name} <span className="text-foreground-500 text-lg font-normal">· 蘑菇视频网页版</span>
              </h1>
              <p className="text-foreground-600 text-sm md:text-base">{activeCategory?.description}</p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-nowrap">
              {categoriesFull.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer no-underline ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {activeCategory?.subCategories && (
              <div className="flex items-center gap-2 mb-8 flex-wrap">
                {activeCategory.subCategories.map((sub) => (
                  <span
                    key={sub}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-background-100 text-foreground-600 border border-background-200/70 cursor-pointer hover:bg-secondary-100 hover:text-secondary-700 transition-colors duration-200"
                  >
                    {sub}
                  </span>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {categoryVideos.map((video) => (
                <div
                  key={video.id}
                  className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={video.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/60 via-transparent to-transparent"></div>
                    <div className="absolute top-2 right-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-500 text-secondary-950 whitespace-nowrap">
                        {video.rating}
                      </span>
                    </div>
                    <div className="absolute bottom-2 left-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-black/40 text-background-50 backdrop-blur-sm whitespace-nowrap">
                        {video.episodes}
                      </span>
                    </div>
                  </div>
                  <div className="p-3.5">
                    <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors duration-200">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-600 mt-1.5 line-clamp-2">{video.description}</p>
                    <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-background-200/70">
                      <div className="flex items-center gap-1.5">
                        {video.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <span className="text-[10px] text-foreground-500">{video.views}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {categoryVideos.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-video-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容，请浏览其他分类</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}